import { FC } from "react"
import { useModals } from "@/modals"
import type { ModalType } from "@/modals/constructor"
import { COMMON_MODALS } from "@/modals/constructor/common"
import { Button } from "@/ui/Button"
import styles from "./OpenAnyModalSection.module.scss"
import { MODAL_MOCKS } from "./modalMocks"

const EXCLUDED: ReadonlySet<ModalType> = new Set<ModalType>(["pageLoading"])

export const OpenAnyModalSection: FC = () => {
  const { openModal, closeModal } = useModals()

  // Built lazily inside render to avoid a circular-import TDZ:
  // src/modals/constructor/common/index.ts imports DevToolsModal, which
  // imports this file. Reading COMMON_MODALS at module top-level would hit
  // it before initialization.
  const groups: { title: string; keys: ModalType[] }[] = [
    { title: "common", keys: Object.keys(COMMON_MODALS) as ModalType[] }
  ]

  const handleOpen = (type: ModalType) => {
    closeModal("devTools")
    const params = MODAL_MOCKS[type]
    if (params === undefined) {
      // openModal's overload allows omitting args when params type is undefined
      ;(openModal as (t: ModalType) => void)(type)
    } else {
      ;(openModal as (t: ModalType, p: unknown) => void)(type, params)
    }
  }

  return (
    <section className={styles.OpenAnyModalSection}>
      <h3 className={styles.OpenAnyModalSection_title}>Open Any Modal</h3>
      {groups.map((group) => {
        const visibleKeys = group.keys.filter((key) => !EXCLUDED.has(key))
        if (visibleKeys.length === 0) {
          return null
        }
        return (
          <div key={group.title} className={styles.OpenAnyModalSection_group}>
            <h4 className={styles.OpenAnyModalSection_groupTitle}>
              {group.title}
            </h4>
            <div className={styles.OpenAnyModalSection_buttons}>
              {visibleKeys.map((key) => {
                const mocked = key in MODAL_MOCKS
                return (
                  <Button
                    className={styles.OpenAnyModalSection_button}
                    onClick={() => handleOpen(key)}
                    key={key}
                  >
                    {key}
                    {mocked ? " (mocked)" : ""}
                  </Button>
                )
              })}
            </div>
          </div>
        )
      })}
    </section>
  )
}
